import { useState } from 'react';

import { ApiError } from '../../lib/api.js';
import { signIn } from '../../lib/session.js';
import { PasswordInput } from '../../components/PasswordInput.js';
import '../checkout/begin.css';

/**
 * Sign in.
 *
 * The password is the one chosen at checkout. A wrong pair comes back as a single, unrevealing
 * message — the server never says whether it was the email or the password, and neither does this.
 * On success the reflection opens.
 */
export function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await signIn(email.trim(), password);
      // A full navigation, so the app boots signed-in rather than threading state through a router.
      window.location.assign('/');
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 401
          ? 'That email and password did not match. Please try again.'
          : 'Something went wrong signing in. Please try again in a moment.',
      );
      setBusy(false);
    }
  };

  return (
    <div className="begin__centered">
      <p className="begin__eyebrow">Welcome back</p>
      <h1 className="complete__title">Sign in</h1>
      <p className="complete__body">Use the email and password you chose when you purchased.</p>

      <form
        className="login"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <label className="begin__field">
          <span className="begin__label">Email</span>
          <input
            className="begin__input"
            type="email"
            value={email}
            autoComplete="email"
            onChange={(event) => setEmail(event.target.value)}
          />
        </label>

        <label className="begin__field">
          <span className="begin__label">Password</span>
          <PasswordInput value={password} autoComplete="current-password" onChange={setPassword} />
        </label>

        {error ? <p className="begin__error">{error}</p> : null}

        <button
          type="submit"
          className="button button--primary begin__submit"
          disabled={busy || email.trim() === '' || password === ''}
        >
          {busy ? 'Signing in…' : 'Sign in'}
        </button>

        <p className="begin__secure">
          <a href="/forgot">Forgot your password?</a>
        </p>
      </form>
    </div>
  );
}
